import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  DatePicker,
  Row,
  Col,
  Upload,
  Progress,
  Switch,
  Tag,
  Grid,
  Tooltip
} from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ClearOutlined, CloudUploadOutlined, FilterOutlined } from '@ant-design/icons';
import { versionAPI, ossAPI } from '../utils/api';
import { Version, CreateVersionRequest, UpdateVersionRequest } from '../types';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const Versions: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [versions, setVersions] = useState<Version[]>([]);
  const [filteredVersions, setFilteredVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVersion, setEditingVersion] = useState<Version | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  // 搜索条件状态
  const [searchFilters, setSearchFilters] = useState({
    id: '',
    versionCode: '',
    description: '',
    installPath: '',
    dateRange: null as any
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  // 当versions数据变化时，重新应用过滤器
  useEffect(() => {
    applyFilters(searchFilters);
  }, [versions]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await versionAPI.getVersions();
      setVersions(data);
      setFilteredVersions(data); // 初始化过滤后的数据
    } catch (err: any) {
      setError(err.message || '获取版本列表失败');
      console.error('获取版本列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 搜索过滤逻辑
  const applyFilters = (filters: typeof searchFilters) => {
    let filtered = [...versions];

    // ID过滤
    if (filters.id) {
      filtered = filtered.filter(version =>
        version.id.toString().includes(filters.id)
      );
    }

    // 版本号过滤
    if (filters.versionCode) {
      filtered = filtered.filter(version =>
        version.versionCode.toString().includes(filters.versionCode)
      );
    }

    // 描述过滤
    if (filters.description) {
      filtered = filtered.filter(version =>
        (version.description || '').toLowerCase().includes(filters.description.toLowerCase())
      );
    }

    // 安装路径过滤
    if (filters.installPath) {
      filtered = filtered.filter(version =>
        (version.installPath || '').toLowerCase().includes(filters.installPath.toLowerCase())
      );
    }

    // 日期范围过滤
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(version => {
        const createdAt = dayjs(version.createdAt);
        return createdAt.isAfter(startDate.startOf('day')) &&
               createdAt.isBefore(endDate.endOf('day'));
      });
    }

    setFilteredVersions(filtered);
  };

  // 处理搜索
  const handleSearch = (values: any) => {
    const filters = {
      id: values.id || '',
      versionCode: values.versionCode || '',
      description: values.description || '',
      installPath: values.installPath || '',
      dateRange: values.dateRange || null
    };
    setSearchFilters(filters);
    applyFilters(filters);
  };

  // 重置搜索
  const handleResetSearch = () => {
    const emptyFilters = {
      id: '',
      versionCode: '',
      description: '',
      installPath: '',
      dateRange: null
    };
    setSearchFilters(emptyFilters);
    searchForm.resetFields();
    setFilteredVersions(versions);
  };

  const showModal = (version?: Version) => {
    setEditingVersion(version || null);
    setModalVisible(true);

    // 使用 setTimeout 确保 Modal 完全渲染后再设置表单值
    setTimeout(() => {
      if (version) {
        form.setFieldsValue({
          versionCode: version.versionCode,
          description: version.description,
          installPath: version.installPath,
          forceUpdate: version.forceUpdate !== 0, // 只有明确为0时才是false
        });
      } else {
        form.resetFields();
        // 默认强制更新
        form.setFieldValue('forceUpdate', true);
      }
    }, 0);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingVersion(null);
    setUploading(false);
    setUploadProgress(0);
    form.resetFields();
  };

  // 上传 APK 到 OSS
  const handleUploadApk = async (file: File, versionCode: number) => {
    try {
      setUploading(true);
      setUploadProgress(10);
      
      message.loading({ content: '正在上传APK...', key: 'upload' });
      
      // 模拟进度（实际上传不支持进度回调时使用）
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const result = await ossAPI.uploadApk(file, versionCode);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // 设置上传后的 URL 到表单
      form.setFieldValue('installPath', result.url);
      
      message.success({ content: `APK上传成功！文件大小: ${(result.size / 1024 / 1024).toFixed(2)} MB`, key: 'upload' });
      
    } catch (err: any) {
      message.error({ content: err.response?.data?.error || err.message || '上传失败', key: 'upload' });
      console.error('上传失败:', err);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      
      // 将 forceUpdate 布尔值转换为数据库的 0/1 格式
      const forceUpdateValue = values.forceUpdate ? 1 : 0;
      
      if (editingVersion) {
        // 更新版本
        const updateData: UpdateVersionRequest = {
          id: editingVersion.id,
          versionCode: values.versionCode,
          description: values.description,
          installPath: values.installPath,
          forceUpdate: forceUpdateValue
        };
        await versionAPI.updateVersion(updateData);
        message.success('版本更新成功');
      } else {
        // 创建版本
        const createData: CreateVersionRequest = {
          versionCode: values.versionCode,
          description: values.description,
          installPath: values.installPath,
          forceUpdate: forceUpdateValue
        };
        await versionAPI.createVersion(createData);
        message.success('版本创建成功');
      }
      
      setModalVisible(false);
      setEditingVersion(null);
      form.resetFields();
      await fetchVersions();
    } catch (err: any) {
      message.error(err.message || '操作失败');
      console.error('操作失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await versionAPI.deleteVersion(id);
      message.success('版本删除成功');
      await fetchVersions();
    } catch (err: any) {
      message.error(err.message || '删除失败');
      console.error('删除失败:', err);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: isMobile ? 60 : 80,
      sorter: (a: Version, b: Version) => a.id - b.id,
    },
    {
      title: '版本号',
      dataIndex: 'versionCode',
      key: 'versionCode',
      width: isMobile ? 100 : undefined,
      sorter: (a: Version, b: Version) => a.versionCode - b.versionCode,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: isMobile ? 100 : undefined,
      render: (text: string) => text || '-',
      sorter: (a: Version, b: Version) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      },
    },
    {
      title: '安装路径',
      dataIndex: 'installPath',
      key: 'installPath',
      width: isMobile ? 120 : undefined,
      ellipsis: true,
      render: (text: string) => text ? (
        <Tooltip title={text}>
          <span style={{ 
            display: 'block',
            maxWidth: isMobile ? 100 : 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </span>
        </Tooltip>
      ) : '-',
      sorter: (a: Version, b: Version) => {
        const pathA = a.installPath || '';
        const pathB = b.installPath || '';
        return pathA.localeCompare(pathB);
      },
    },
    {
      title: '强制更新',
      dataIndex: 'forceUpdate',
      key: 'forceUpdate',
      width: isMobile ? 80 : 100,
      render: (value: number | undefined) => {
        // 只有明确为0时才不强制更新，其他情况（1/null/undefined）都强制更新
        const isForce = value !== 0;
        return isForce ? (
          <Tag color="red">{isMobile ? '强制' : '强制更新'}</Tag>
        ) : (
          <Tag color="blue">{isMobile ? '提醒' : '提醒更新'}</Tag>
        );
      },
      sorter: (a: Version, b: Version) => {
        const forceA = a.forceUpdate !== 0 ? 1 : 0;
        const forceB = b.forceUpdate !== 0 ? 1 : 0;
        return forceA - forceB;
      },
    },
    ...(!isMobile ? [{
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: Version, b: Version) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    }] : []),
    {
      title: '操作',
      key: 'actions',
      width: isMobile ? 80 : 150,
      render: (_: any, record: Version) => (
         (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              size="small"
            >
              编辑
            </Button>
            <Popconfirm
              title="确定要删除这个版本吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
                size="small"
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        )
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📦 版本列表
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: isMobile ? 12 : 14 }}>
          管理系统版本信息
        </p>
      </div>

      <Card
        className="content-card"
        extra={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            {isMobile && (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setShowSearchForm(!showSearchForm)}
                type={showSearchForm ? 'primary' : 'default'}
              >
                筛选
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchVersions}
              loading={loading}
            >
              {isMobile ? '' : '刷新'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              {isMobile ? '新增' : '新增版本'}
            </Button>
          </Space>
        }
      >
        {/* 搜索表单 */}
        {(showSearchForm || !isMobile) && (
        <Card
          size="small"
          title={isMobile ? '筛选条件' : '搜索条件'}
          style={{ marginBottom: 16 }}
          styles={{ body: { paddingBottom: 0 } }}
        >
          <Form
            form={searchForm}
            onFinish={handleSearch}
            layout={isMobile ? 'vertical' : 'inline'}
            colon={false}
          >
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="id"
                  label="ID"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入ID"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="versionCode"
                  label="版本号"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入版本号"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="description"
                  label="描述"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入描述关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Form.Item
                  name="installPath"
                  label="安装路径"
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder="请输入路径关键词"
                    allowClear
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[12, 0]} style={{ width: '100%' }}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="dateRange"
                  label="创建时间"
                  style={{ marginBottom: 8 }}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    placeholder={['开始日期', '结束日期']}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item label={isMobile ? undefined : ' '} style={{ marginBottom: 8 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      htmlType="submit"
                    >
                      搜索
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetSearch}
                    >
                      重置
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        )}
        <Table
          columns={columns}
          dataSource={filteredVersions}
          rowKey="id"
          loading={loading}
          scroll={{ x: isMobile ? 600 : undefined }}
          pagination={{
            defaultPageSize: 100,
            pageSizeOptions: ['50', '100', '200', '500'],
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            showTotal: (total, range) => isMobile ? `${total}条` : `第 ${range?.[0]}-${range?.[1]} 条，共 ${total} 条记录`,
            size: isMobile ? 'small' : 'default'
          }}
          className="version-table"
        />
      </Card>

      <Modal
        title={editingVersion ? '编辑版本' : '新增版本'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="versionCode"
            label="版本号"
            rules={[
              { required: true, message: '请输入版本号' },
              { type: 'number', min: 1, message: '版本号必须大于0' }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder="请输入版本号，如：102302166"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入版本描述"
            />
          </Form.Item>

          <Form.Item
            name="installPath"
            label="安装路径"
          >
            <Input
              placeholder="请输入安装路径，或使用下方上传按钮"
              addonAfter={
                <Upload
                  accept=".apk"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const versionCode = form.getFieldValue('versionCode');
                    if (!versionCode) {
                      message.error('请先填写版本号');
                      return false;
                    }
                    handleUploadApk(file, versionCode);
                    return false;
                  }}
                  disabled={uploading}
                >
                  <Button
                    type="link"
                    icon={<CloudUploadOutlined />}
                    loading={uploading}
                    style={{ padding: 0 }}
                  >
                    {uploading ? '上传中...' : '上传APK'}
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          <Form.Item
            name="forceUpdate"
            label="强制更新"
            valuePropName="checked"
            extra="开启后设备会直接重启更新，关闭后会弹框提醒用户是否重启"
          >
            <Switch 
              checkedChildren="强制" 
              unCheckedChildren="提醒"
              defaultChecked
            />
          </Form.Item>

          {uploading && (
            <Form.Item>
              <Progress percent={uploadProgress} size="small" status="active" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
              >
                {editingVersion ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Versions;
